import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { TextField, Button, Alert, Box, Typography } from '@mui/material';
import AuthCard from '../../components/AuthCard';
import { useAuth } from '../../auth/AuthContext';
import { formatAuthError } from '../../auth/formatAuthError';

const BTN = { bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 };
const LINK = { color: '#3B82F6' };

export default function ForgotPasswordPage() {
  const { forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const { register, handleSubmit, formState: { isSubmitting }, reset } = useForm();

  const onRequest = async ({ email: e }) => {
    setError('');
    await forgotPassword(e);
    setEmail(e);
    setStep('reset');
    reset();
  };

  const onReset = async ({ code, newPassword }) => {
    setError('');
    try {
      await confirmForgotPassword(email, code, newPassword);
      navigate('/login');
    } catch (e) {
      setError(formatAuthError(e.message) || 'Reset failed.');
    }
  };

  if (step === 'request') {
    return (
      <AuthCard title="Reset your password" subtitle="We'll send a code to your email">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit(onRequest)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Email" type="email" size="small" fullWidth {...register('email', { required: true })} />
          <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={BTN}>
            {isSubmitting ? 'Sending…' : 'Send reset code'}
          </Button>
        </Box>
        <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
          <Link to="/login" style={LINK}>Back to sign in</Link>
        </Typography>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Enter your new password" subtitle={`Code sent to ${email}`}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={handleSubmit(onReset)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Confirmation code" size="small" fullWidth {...register('code', { required: true })} />
        <TextField label="New password" type="password" size="small" fullWidth
          {...register('newPassword', { required: true, minLength: 8 })} />
        <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={BTN}>
          {isSubmitting ? 'Resetting…' : 'Reset password'}
        </Button>
      </Box>
    </AuthCard>
  );
}
